import React from 'react';
import { useSelector } from 'react-redux';
import { Flex } from '@adobe/react-spectrum';

import { BaseDeckPane } from './BaseDeckPane';
import { getPrettyLabel } from '../../../data/CategoryPrettyDisplayUtils';
import './PowerLevelPane.css';

const POWER_TIERS = [
    { min: 9, label: 'Competitive', tone: 'alert' },
    { min: 7, label: 'Optimized', tone: 'alert' },
    { min: 5, label: 'Focused Casual', tone: 'watch' },
    { min: 3, label: 'Casual', tone: 'ok' },
    { min: 0, label: 'Exhibition', tone: 'ok' },
];

const PATTERN_LABELS = {
    isControlDeck: { label: 'Control', tone: 'cool' },
    isPureControlDeck: { label: 'Pure Control', tone: 'cool' },
    isStaxWithoutCombo: { label: 'Stax (no combo)', tone: 'cool' },
    isEnablerPileDeck: { label: 'Enabler Pile', tone: 'warn' },
    isFocusedCasual: { label: 'Focused Casual', tone: 'ok' },
    isComboCasualBuild: { label: 'Combo Casual', tone: 'ok' },
    isJankyCombo: { label: 'Janky Combo', tone: 'warn' },
    isRedundantComboDeck: { label: 'Redundant Combo', tone: 'alert' },
    isIntentionalComboBuild: { label: 'Intentional Combo', tone: 'alert' },
    hasStrongComboBackbone: { label: 'Combo Backbone', tone: 'alert' },
    hasDenseEnablerComboCore: { label: 'Dense Enabler Core', tone: 'alert' },
    hasDisruptiveStrategy: { label: 'Disruptive', tone: 'cool' },
    hasUnreliableWincons: { label: 'Unreliable Wincons', tone: 'warn' },
    lacksCompetitiveCore: { label: 'Lacks Competitive Core', tone: 'warn' },
    hasEliteCompetitiveCoreForCompression: { label: 'Elite Competitive Core', tone: 'alert' },
    isSlowCompetitiveDeck: { label: 'Slow Competitive', tone: 'warn' },
    isGoodstuffValueDeck: { label: 'Goodstuff / Value', tone: 'warn' },
    isBudgetControlShell: { label: 'Budget Control Shell', tone: 'cool' },
    isLandsMatterPrecon: { label: 'Lands-Matter Precon', tone: 'ok' },
    isPreconWithAccidentalCombos: { label: 'Precon + Accidental Combos', tone: 'warn' },
    isFocusedVoltronForCompression: { label: 'Focused Voltron', tone: 'ok' },
    isCEDHStaxBuild: { label: 'cEDH Stax', tone: 'alert' },
    isCEDHStaxForPenalty: { label: 'cEDH Stax (penalty)', tone: 'alert' },
    hasMultiWinconDensity: { label: 'Multi-Wincon', tone: 'alert' },
};

const COMPONENT_METRICS = [
    { key: 'tutorComponent', label: 'Tutors' },
    { key: 'fastManaComponent', label: 'Fast Mana' },
    { key: 'efficiencyComponent', label: 'Efficiency' },
    { key: 'synergyComponent', label: 'Synergy' },
    { key: 'winconComponent', label: 'Wincons' },
    { key: 'comboComponent', label: 'Combo' },
    { key: 'interactionRatio', label: 'Interaction' },
    { key: 'manabaseRatio', label: 'Manabase' },
];

function humanizeKey(key) {
    if (!key) return '';
    return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (c) => c.toUpperCase())
        .trim();
}

function formatSignedDelta(value) {
    if (value == null || Number.isNaN(value)) return '';
    const sign = value > 0 ? '+' : value < 0 ? '−' : '±';
    return `${sign}${Math.abs(value).toFixed(3)}`;
}

function PatternChip({ flagKey, active }) {
    if (!active) return null;
    const meta = PATTERN_LABELS[flagKey];
    if (!meta) return null;
    return <span className={`powerPatternChip powerPatternChip--${meta.tone}`}>{meta.label}</span>;
}

function AdjustmentRow({ adjustment }) {
    const delta = adjustment?.biasChange ?? 0;
    const tone = delta > 0.005 ? 'up' : delta < -0.005 ? 'down' : 'flat';
    return (
        <div className={`powerAdjustRow powerAdjustRow--${tone}`}>
            <span className="powerAdjustArrow" aria-hidden="true">
                {tone === 'up' ? '▲' : tone === 'down' ? '▼' : '■'}
            </span>
            <span className="powerAdjustName">{adjustment?.adjustment || 'Adjustment'}</span>
            <span className="powerAdjustDelta">{formatSignedDelta(delta)}</span>
            {adjustment?.reason && <span className="powerAdjustReason">{adjustment.reason}</span>}
        </div>
    );
}

const SUB_PILLARS = [
    {
        key: 'consistency',
        label: 'Consistency',
        hint: 'How reliably the deck assembles its plan',
        subs: ['tutors', 'draw', 'recursion', 'topdeck', 'multipliers'],
    },
    {
        key: 'efficiency',
        label: 'Efficiency',
        hint: 'Speed and mana density',
        subs: ['fastmana', 'ramp', 'costReduction'],
    },
    {
        key: 'interaction',
        label: 'Interaction',
        hint: 'Answers, disruption, and protection',
        subs: [
            'spotRemoval',
            'boardWipes',
            'counters',
            'stax',
            'taxes',
            'graveyard',
            'evasion',
            'otherControl',
            'discard',
        ],
    },
    {
        key: 'winConditions',
        label: 'Win Conditions',
        hint: 'Dedicated finishers and combo lines',
        subs: [
            'combos',
            'combat',
            'wincon',
            'wincon_anthem',
            'wincon_burn',
            'wincon_groupslug',
            'wincon_poison',
            'wincon_stompy',
            'wincon_tokens',
            'extraTurns',
            'extraCombats',
            'overrun',
            'voltron',
        ],
    },
];

function powerTier(rating) {
    const r = Number(rating);
    if (Number.isNaN(r)) return POWER_TIERS[POWER_TIERS.length - 1];
    for (const tier of POWER_TIERS) {
        if (r >= tier.min) return tier;
    }
    return POWER_TIERS[POWER_TIERS.length - 1];
}

function pillarIndicator(ratio) {
    if (ratio >= 1.25) return 'above';
    if (ratio <= 0.75) return 'below';
    return 'match';
}

function PillarCard({ pillar, scoring, baseline }) {
    const totalScore = scoring?.[pillar.key]?.score || 0;
    const ratio = baseline > 0 ? totalScore / baseline : 0;
    const pct = Math.round(ratio * 100);
    const indicator = pillarIndicator(ratio);

    const rows = pillar.subs
        .map((s) => ({
            key: s,
            label: getPrettyLabel(s),
            score: scoring?.[s]?.score || 0,
        }))
        .filter((r) => r.score > 0)
        .sort((a, b) => b.score - a.score);
    const maxScore = rows.reduce((m, r) => Math.max(m, r.score), 0);

    return (
        <section className={`powerPillarCard powerPillarCard--${indicator}`}>
            <header className="powerPillarHeader">
                <div className="powerPillarTitleBlock">
                    <span className="powerPillarTitle">{pillar.label}</span>
                    <span className="powerPillarHint">{pillar.hint}</span>
                </div>
                <div className="powerPillarScoreBlock">
                    <span className="powerPillarScore">{Math.round(totalScore)}</span>
                    <span className="powerPillarBaseline">vs {Math.round(baseline)}</span>
                    <span className={`powerPillarDelta powerPillarDelta--${indicator}`}>
                        {indicator === 'above' ? '▲' : indicator === 'below' ? '▼' : '═'} {pct}%
                    </span>
                </div>
            </header>
            <div className="powerPillarSubList">
                {rows.length === 0 ? (
                    <span className="powerPillarEmpty">No dedicated cards scored in this pillar.</span>
                ) : (
                    rows.map((r) => {
                        const w = maxScore > 0 ? Math.round((r.score / maxScore) * 100) : 0;
                        return (
                            <div className="powerPillarSubRow" key={r.key}>
                                <span className="powerPillarSubLabel">{r.label}</span>
                                <div className="powerPillarSubBarTrack">
                                    <div className="powerPillarSubBarFill" style={{ width: `${w}%` }} />
                                </div>
                                <span className="powerPillarSubScore">{Math.round(r.score)}</span>
                            </div>
                        );
                    })
                )}
            </div>
        </section>
    );
}

export function PowerLevelPane() {
    const deck = useSelector((state) => state.deck.deck);
    const cards = useSelector((state) => state.deck.cards);

    const isFringe =
        deck?.details?.powerLevel?.ratings?.fringeCEDH === true && parseFloat(deck?.powerLevelRating) >= 8.7;
    const useCEDH = deck?.details?.powerLevel?.ratings?.inferredType === 'spike' || isFringe;

    const [useCEDHPowerLevels, setUseCEDHPowerLevels] = React.useState(useCEDH);
    const type = useCEDHPowerLevels ? 'spike' : 'casual';

    const scoring = deck?.details?.powerLevel?.scoring || {};
    const baseValues = deck?.details?.powerLevel?.baseValues?.[type] || {};
    const ratings = deck?.details?.powerLevel?.ratings || {};

    const powerLevel = Number(deck?.powerLevelRating) || 0;
    // Truncate (not round) to 1 decimal so 5.97 → 5.9 rather than 6.0. Rounding up
    // past the integer bracket on the assessment gauge looked inconsistent.
    const powerLevelDisplay = (Math.floor(powerLevel * 10) / 10).toFixed(1);
    const tier = powerTier(powerLevel);
    const pct = Math.max(0, Math.min(100, (powerLevel / 10) * 100));

    const bias = deck?.details?.powerLevel?.biasExplanation || {};
    const adjustments = bias.adjustments || [];
    const compression = bias.intermediateValues?.compressionDetails || {};
    const detectedPatterns = compression.detectedPatterns || {};
    const metrics = compression.metrics || {};
    const boosts = compression.boosts || {};
    const penalties = compression.penalties || {};
    const caps = compression.caps || {};

    const compressionItems = [
        ...Object.entries(boosts)
            .filter(([, v]) => v > 0.001)
            .map(([k, v]) => ({ key: k, value: v, kind: 'boost' })),
        ...Object.entries(penalties)
            .filter(([, v]) => v < -0.001)
            .map(([k, v]) => ({ key: k, value: v, kind: 'penalty' })),
    ].sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

    const activePatterns = Object.keys(detectedPatterns)
        .filter((k) => detectedPatterns[k] && PATTERN_LABELS[k])
        .slice(0, 16);

    const componentMetrics = COMPONENT_METRICS.map((m) => ({
        ...m,
        value: Number(metrics[m.key]),
    })).filter((m) => !Number.isNaN(m.value));

    const capsActive = Object.entries(caps)
        .filter(([k, v]) => v === true && k.endsWith('Capped'))
        .map(([k]) => humanizeKey(k.replace(/Capped$/, '')));

    const getBlurbs = () => ({
        tutors: {
            headerLabel: 'Tutors',
            tooMany: { casual: '', spike: '' },
            tooFew: {
                casual: '',
                spike: `Tutors greatly increase consistency. ${
                    deck?.details?.powerLevel?.ratings?.spike?.stax >= 7
                        ? 'You appear to have a fair amount of stax effects... do any of these punish other people for tutoring?'
                        : 'Consider adding more if that is an option.'
                }`,
            },
        },
        answers: {
            headerLabel: 'Interaction',
            tooMany: {
                casual: 'Casual players typically run very little interaction. If you are running much more than everyone else, you will most likely be archenemy.',
                spike: '',
            },
            tooFew: {
                casual: '',
                spike:
                    deck?.details?.powerLevel?.ratings?.spike?.stax >= 10
                        ? 'You have a lot of stax effects, which is great. However, your instant-speed answers appear to be low. Consider increasing this if you have issues closing out games.'
                        : 'Competitive players run a high amount of interaction in order to protect their win conditions. If you are having issues closing out games, consider increasing this.',
            },
        },
        combos: {
            headerLabel: 'Combos',
            tooMany: {
                casual: '',
                spike:
                    deck?.details?.powerLevel?.ratings?.spike?.answers <= 7
                        ? 'Are you running enough interaction to protect your combos?'
                        : '',
            },
            tooFew: { casual: '', spike: '' },
        },
        stax: {
            headerLabel: 'Stax',
            tooMany: {
                casual: 'People looking for casual games tend to strongly dislike stax effects as it prevents them from having fun. Additionally, casual players typically run far less interaction than competitive players, so you are essentially playing these effects into a vacuum.',
                spike: '',
            },
            tooFew: { casual: '', spike: '' },
        },
        fastmana: {
            headerLabel: 'Fast mana',
            tooMany: {
                casual: 'Having a lot of fast mana pieces gives you a board state that is often way ahead of everyone else in a casual pod. Consider lowering this if you are primarily playing this in non cEDH / high power settings.',
                spike: '',
            },
            tooFew: {
                casual: '',
                spike: 'Fast mana pieces like Mana Crypt are pretty necessary for higher power games, as they increase your ability to quickly assemble a board state. Since other people will be running a lot of these, you may be at a disadvantage.',
            },
        },
    });

    const getDiscussionPoints = () => {
        const list = [];
        const casualBase = deck?.details?.powerLevel?.baseValues?.casual || {};
        Object.keys(casualBase)?.forEach((category) => {
            const actual = deck?.details?.powerLevel?.scoring?.[category]?.score;
            const baseline = casualBase[category];
            const blurbMap = getBlurbs()?.[category];
            if (!blurbMap || !actual || !baseline) return;

            let direction = null;
            if (actual / baseline > 2) direction = 'tooMany';
            if (actual / baseline <= 0.6) direction = 'tooFew';
            if (!direction) return;

            const soapbox = blurbMap?.[direction]?.casual;
            if (soapbox && soapbox !== '') {
                list.push({
                    category,
                    blurb: soapbox,
                    header: blurbMap.headerLabel,
                });
            }
        });
        return list;
    };

    const getCategories = () => {
        const list = {};

        Object.keys(deck?.details?.powerLevel?.baseValues?.[type] || {})
            ?.filter((category) => {
                switch (category) {
                    case 'fastmana':
                    case 'taxes':
                    case 'stax':
                    case 'tutors':
                    case 'draw':
                    case 'answers':
                    case 'ramp':
                    case 'wincon':
                    case 'combo':
                        return false;
                    default:
                        return true;
                }
            })
            ?.forEach((category) => {
                let item = deck?.details?.powerLevel?.scoring?.[category];
                if (category === 'landbase' || category === 'synergy') return;
                if (!item) return;

                let subCategoryList = {};
                let items = [];
                const label = getPrettyLabel(category);

                if (item?.subCategories?.length > 0) {
                    item.subCategories.forEach((subCategory) => {
                        if (subCategory === 'landbase') return;
                        const subLabel = getPrettyLabel(subCategory);
                        const subList = deck?.details?.powerLevel?.scoring?.[subCategory]?.list || {};

                        if (subCategory === 'combos') {
                            subCategoryList[subCategory] = {
                                label: subLabel,
                                id: subCategory,
                                count: Object.keys(subList).length,
                                list: Object.keys(subList).map((_key) => {
                                    const _entry = subList[_key];
                                    return {
                                        ..._entry,
                                        type: 'combo',
                                        cards: (_entry?.cards || []).map((__card) => cards?.[__card]),
                                    };
                                }),
                                parentCategory: category,
                            };
                            items = items.concat(
                                Object.keys(subList).map((_key) => {
                                    const _entry = subList[_key];
                                    return {
                                        ..._entry,
                                        type: 'combo',
                                        cards: (_entry?.cards || []).map((__card) => cards?.[__card]),
                                    };
                                })
                            );
                        } else {
                            subCategoryList[subCategory] = {
                                label: subLabel,
                                id: subCategory,
                                count: Object.keys(subList).length,
                                list: Object.keys(subList).map((_key) => {
                                    const _card = subList[_key];
                                    return { ..._card, ...cards?.[_card?.id] };
                                }),
                                parentCategory: category,
                            };
                            items = items.concat(
                                Object.keys(subList).map((_key) => {
                                    const _card = subList[_key];
                                    return {
                                        ..._card,
                                        ...cards?.[_card?.id],
                                        id: _card?.id,
                                    };
                                })
                            );
                        }
                    });

                    const itemsHash = {};
                    items.forEach((it) => {
                        if (!itemsHash[it.id]) {
                            itemsHash[it.id] = it;
                        } else {
                            itemsHash[it.id].score = (
                                parseFloat(itemsHash[it.id].score) + parseFloat(it.score)
                            ).toFixed(1);
                        }
                    });
                    items = Object.keys(itemsHash).map((k) => itemsHash[k]);
                } else {
                    items = Object.keys(scoring?.[category]?.list || {}).map((key) => {
                        const it = scoring[category].list[key];
                        return { ...it, ...cards?.[it?.id] };
                    });
                }

                if (items?.length > 1) {
                    items = items.sort((a, b) => ((b?.score || 0) > (a?.score || 0) ? 1 : -1));
                }

                list[category] = {
                    label,
                    id: category,
                    count: items?.length || 0,
                    list: items,
                    color: '#FFFFFF',
                    subCategories: subCategoryList,
                };
            });

        Object.keys(list).forEach((key) => {
            if ((list[key]?.list?.length || 0) < 1) {
                delete list[key];
            } else {
                Object.keys(list[key]?.subCategories || {}).forEach((subKey) => {
                    if ((list[key].subCategories[subKey]?.list?.length || 0) < 1) {
                        delete list[key].subCategories[subKey];
                    }
                });
            }
        });

        return list;
    };

    const discussion = getDiscussionPoints();

    const pillarsToRender = SUB_PILLARS.filter((p) => (baseValues[p.key] || 0) > 0 || (scoring[p.key]?.score || 0) > 0);

    return (
        <BaseDeckPane
            title={'Power Levels'}
            fieldName="name"
            content={<div />}
            showScore={true}
            categories={getCategories()}
        >
            <Flex direction="column" gap="size-200">
                <section className="powerHero">
                    <div className="powerHeroTop">
                        <span className="powerHeroEyebrow">Power Level</span>
                        <span className={`powerHeroTier powerHeroTier--${tier.tone}`}>{tier.label}</span>
                    </div>
                    <div className="powerHeroScoreRow">
                        <span className="powerHeroScoreValue">{powerLevelDisplay}</span>
                        <span className="powerHeroScoreUnit">/ 10</span>
                    </div>
                    <div className="powerHeroGauge">
                        <div className="powerHeroGaugeTrack">
                            <div className="powerHeroGaugeFill" style={{ width: `${pct}%` }} />
                            {[3, 5, 7, 9].map((t) => (
                                <span key={t} className="powerHeroGaugeTick" style={{ left: `${(t / 10) * 100}%` }} />
                            ))}
                            <div className="powerHeroGaugePointer" style={{ left: `${pct}%` }} aria-hidden="true" />
                        </div>
                        <div className="powerHeroGaugeScale">
                            <span>0</span>
                            <span>3</span>
                            <span>5</span>
                            <span>7</span>
                            <span>9</span>
                            <span>10</span>
                        </div>
                    </div>
                </section>

                <section className="powerContext">
                    <div className="powerContextMetric">
                        <span className="powerContextLabel">Inferred type</span>
                        <span className="powerContextValue">
                            {ratings.inferredType ? ratings.inferredType.toUpperCase() : '—'}
                        </span>
                    </div>
                    <div className="powerContextMetric">
                        <span className="powerContextLabel">Commander tier</span>
                        <span className="powerContextValue">
                            {ratings.commanderTier != null ? `T${ratings.commanderTier}` : '—'}
                        </span>
                    </div>
                    {/*<div className="powerContextMetric">*/}
                    {/*    <span className="powerContextLabel">Card quality</span>*/}
                    {/*    <span className="powerContextValue">{ratings.cardQuality ?? '—'}</span>*/}
                    {/*</div>*/}
                    {ratings.fringeCEDH && (
                        <div className="powerContextMetric powerContextMetric--flag">
                            <span className="powerContextLabel">Flag</span>
                            <span className="powerContextValue">Fringe cEDH</span>
                        </div>
                    )}
                </section>

                <div className="powerBaselineBar">
                    <span className="powerBaselineBarLabel">Compare pillar scores against</span>
                    <div className="powerBaselineSegmented" role="radiogroup" aria-label="Baseline">
                        <button
                            type="button"
                            className={`powerBaselineBtn${!useCEDHPowerLevels ? ' is-active' : ''}`}
                            onClick={() => setUseCEDHPowerLevels(false)}
                            role="radio"
                            aria-checked={!useCEDHPowerLevels}
                        >
                            Casual baseline
                        </button>
                        <button
                            type="button"
                            className={`powerBaselineBtn${useCEDHPowerLevels ? ' is-active' : ''}`}
                            onClick={() => setUseCEDHPowerLevels(true)}
                            role="radio"
                            aria-checked={useCEDHPowerLevels}
                        >
                            cEDH baseline
                        </button>
                    </div>
                </div>

                <div className="powerPillarGrid">
                    {pillarsToRender.map((p) => (
                        <PillarCard key={p.key} pillar={p} scoring={scoring} baseline={baseValues[p.key] || 0} />
                    ))}
                </div>

                {(componentMetrics.length > 0 || activePatterns.length > 0) && (
                    <section className="powerSection">
                        <header className="powerSectionHeader">
                            <span className="powerSectionEyebrow">Deck fingerprint</span>
                            <span className="powerSectionHint">
                                Component densities and shape-detection flags pulled from the biasExplanation — each of
                                these inputs is what compresses or boosts the final power number.
                            </span>
                        </header>
                        {componentMetrics.length > 0 && (
                            <div className="powerComponentGrid">
                                {componentMetrics.map((m) => {
                                    const p = Math.max(0, Math.min(100, Math.round(m.value * 100)));
                                    return (
                                        <div className="powerComponentTile" key={m.key}>
                                            <span className="powerComponentLabel">{m.label}</span>
                                            <div className="powerComponentBarTrack">
                                                <div className="powerComponentBarFill" style={{ width: `${p}%` }} />
                                            </div>
                                            <span className="powerComponentValue">{p}%</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {activePatterns.length > 0 && (
                            <div className="powerPatternChipRow">
                                {activePatterns.map((k) => (
                                    <PatternChip key={k} flagKey={k} active />
                                ))}
                            </div>
                        )}
                    </section>
                )}

                {(compressionItems.length > 0 || adjustments.length > 0 || capsActive.length > 0) && (
                    <section className="powerSection">
                        <header className="powerSectionHeader">
                            <span className="powerSectionEyebrow">Score adjustments</span>
                            <span className="powerSectionHint">
                                Boosts and penalties applied during compression, plus the ordered ledger of every
                                adjustment that reshaped the raw bias into the final power score.
                            </span>
                        </header>
                        {compressionItems.length > 0 && (
                            <div className="powerCompressionGrid">
                                {compressionItems.map((item) => (
                                    <div
                                        key={item.key}
                                        className={`powerCompressionItem powerCompressionItem--${item.kind}`}
                                    >
                                        <span className="powerCompressionItemLabel">{humanizeKey(item.key)}</span>
                                        <span className="powerCompressionItemValue">
                                            {formatSignedDelta(item.value)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {adjustments.length > 0 && (
                            <div className="powerAdjustList">
                                {adjustments.map((adj, idx) => (
                                    <AdjustmentRow key={`${adj.adjustment}_${idx}`} adjustment={adj} />
                                ))}
                            </div>
                        )}
                        {capsActive.length > 0 && (
                            <div className="powerCapsRow">
                                <span className="powerCapsLabel">Caps fired</span>
                                <div className="powerCapsChips">
                                    {capsActive.map((c) => (
                                        <span className="powerCapsChip" key={c}>
                                            {c}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </section>
                )}

                {discussion.length > 0 && type === 'casual' && (
                    <section className="powerDiscussion">
                        <header className="powerDiscussionHeader">
                            <span className="powerDiscussionEyebrow">Rule-zero talking points</span>
                            <span className="powerDiscussionHint">
                                Categories in this deck that sit far above or below typical casual-pod expectations.
                                Worth surfacing before game one.
                            </span>
                        </header>
                        <div className="powerDiscussionList">
                            {discussion.map((item) => (
                                <div className="powerDiscussionItem" key={`${type}_${item.category}`}>
                                    <span className="powerDiscussionItemHeader">{item.header}</span>
                                    <p className="powerDiscussionCopy">{item.blurb}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </Flex>
        </BaseDeckPane>
    );
}
